import {interpolate} from './stringFormat';
import type {LocalizationConfig} from './types';

let config: LocalizationConfig = {dictionaries: {}};
let dictionary: Record<string, string> = {};
let activeLanguageCode = 'en';

export const setDictionary = (dict: Record<string, string>): void => {
    dictionary = dict;
};

const resolveLocal = (languageCode: string): Record<string, string> => {
    const fallback = config.fallbackLanguageCode ?? 'en';

    return config.dictionaries[languageCode] ?? config.dictionaries[fallback] ?? {};
};

export const configure = (cfg: LocalizationConfig): void => {
    config = cfg;

    setDictionary(resolveLocal(cfg.fallbackLanguageCode ?? 'en'));
};

const fetchRemoteLocalization = async (languageCode: string): Promise<void> => {
    if (!config.fetchRemote) return;

    try {
        const remote = await config.fetchRemote(languageCode);
        const isRemote = remote && Object.keys(remote).length > 0;

        if (isRemote) setDictionary({...resolveLocal(languageCode), ...remote});
    } catch (error) {
        console.error(`[Localization] Failed to fetch remote localization for "${languageCode}":`, error);
    }
};

export const downloadDictionary = (languageCode?: string): void => {
    const code = languageCode?.toLowerCase() ?? activeLanguageCode;
    const target = config.dictionaries[code] ?? dictionary;
    const blob = new Blob([JSON.stringify(target, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = `localization_${code}.json`;
    a.click();
    URL.revokeObjectURL(url);
};

export const getLanguageCode = (): string => activeLanguageCode;

export const initLocalization = (languageCode: string): void => {
    const code = languageCode.toLowerCase();

    activeLanguageCode = code;

    setDictionary(resolveLocal(code));
    void fetchRemoteLocalization(code);
};

export const t = (key: string | undefined): string => {
    if (!key) return '';

    return dictionary[key] ?? key;
};

export const formatString = (key: string | undefined, ...values: (string | number | undefined)[]): string =>
    interpolate(t(key), ...values);
